import React, { useState, useRef, useMemo, useEffect } from "react";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { IconButton } from "@mui/material";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import validator from "validator";
import moment from "moment";

import { useAlert } from "../../../context/AlertContext";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import MonthCalendarGrid, { buildMonthDays } from "./MonthCalendarGrid";
import "./memberPopup.css";
// Reused here purely for its .hf-naf-d-field-wrap/.hf-naf-d-popover/.hf-naf-day-grid classes, so
// the Birthday picker's calendar renders with the exact same digit font as the Edit Appointment
// popup's date step — a native <input type="date">'s own popup is OS-rendered and can't be
// restyled to match at all.
import "./newAppointment.css";

// Exact text the backend sends back for a duplicate email (auth.ts addClient / client.ts
// editClient) — matched against so this specific failure highlights the Email field inline
// instead of just showing the generic error toast every other failure gets.
const DUPLICATE_EMAIL_MESSAGE =
  "This email address is already in use. Please enter a different email address.";

// `ApiMethods.apiRequest`'s catch throws either `error.response?.data` (an object, usually
// `{ message }`) or a plain string — normalize both to the message text so callers don't need
// to know which shape they got.
const extractErrorMessage = (error) => {
  if (typeof error === "string") return error;
  if (error && typeof error.message === "string") return error.message;
  return "";
};

// Mobile browsers (iOS Safari in particular) auto-zoom the viewport when an input with
// font-size < 16px receives focus. If that input is still focused when this popup unmounts
// (e.g. the admin taps X/Back right after typing, without tapping elsewhere first), there's no
// blur event to trigger the browser's own zoom-reset — so whatever's left underneath (the
// parent Select Member popup) renders at that stuck-zoomed scale. Blurring the focused element
// ourselves before unmounting closes that gap. (memberPopup.css also raises input font-size to
// 16px on Mobile so this can't be triggered in the first place — this is the defensive backstop
// for any input that's still < 16px, e.g. via inherited styles.)
const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

const PopupTest = ({
  openPopup,
  activeClientInformation,
  refresh,
  setActiveClientInformation,
  onCancel,
  onBack,
}) => {
  const { showAlert, closeAlert } = useAlert();
  // Always mounted only while open (parent conditionally renders this component), so the
  // background (including a parent popup underneath, e.g. Select Member) can't be scrolled
  // while this is up.
  useLockBodyScroll();

  // Email identifies the account, so it's only settable at creation — once a member exists,
  // staff can see it but not retype it, edit it into someone else's, or clear it by accident.
  const isEditMode = !!activeClientInformation;

  const initialValues = {
    gender: {
      male: activeClientInformation?.gender === "Male" || false,
      female: activeClientInformation?.gender === "Female" || false,
    },
    birthday: activeClientInformation?.birthday || "",
    email: activeClientInformation?.email || "",
    first_name: activeClientInformation?.first_name || "",
    last_name: activeClientInformation?.last_name || "",
    phone: activeClientInformation?.phone || "",
    address: activeClientInformation?.address || "",
    nickname: activeClientInformation?.nickname || "",
    notes: activeClientInformation?.notes || "",
    // Height/weight come back from the API as numbers (or null) — kept as strings here so they
    // bind cleanly to a plain <input>, converted back to numbers only when actually submitted
    // (see getChangedFields/onSave).
    height:
      activeClientInformation?.height != null
        ? String(activeClientInformation.height)
        : "",
    weight:
      activeClientInformation?.weight != null
        ? String(activeClientInformation.weight)
        : "",
  };

  const [gender, setGender] = useState(initialValues.gender);
  const [birthday, setBirthday] = useState(initialValues.birthday);
  const [email, setEmail] = useState(initialValues.email);
  const [firstName, setFirstName] = useState(initialValues.first_name);
  const [lastName, setLastName] = useState(initialValues.last_name);
  const [phone, setPhone] = useState(initialValues.phone);
  const [address, setAddress] = useState(initialValues.address);
  const [notes, setNotes] = useState(initialValues.notes);
  const [nickname, setNickname] = useState(initialValues.nickname);
  const [height, setHeight] = useState(initialValues.height);
  const [weight, setWeight] = useState(initialValues.weight);
  const [emailError, setEmailError] = useState("");

  const initials = `${firstName ? firstName[0].toUpperCase() : ""}${
    lastName ? lastName[0].toUpperCase() : ""
  }`;

  // Birthday picker — same MonthCalendarGrid/newAppointment.css markup as the Edit Appointment
  // popup's date step, for font consistency (see the import comment above). Defaults 30 years
  // back rather than the current month — most members are adults, and starting the picker on
  // "today" would mean clicking "prethodni mesec" a few hundred times to reach a birth year.
  const [birthdayPopoverOpen, setBirthdayPopoverOpen] = useState(false);
  const [birthdayViewMonth, setBirthdayViewMonth] = useState(() =>
    initialValues.birthday
      ? moment(initialValues.birthday, "YYYY-MM-DD")
      : moment().subtract(30, "years")
  );
  const birthdayFieldRef = useRef(null);
  const birthdayMonthDays = useMemo(
    () => buildMonthDays(birthdayViewMonth),
    [birthdayViewMonth]
  );

  useEffect(() => {
    if (!birthdayPopoverOpen) return;
    const onMouseDown = (e) => {
      if (
        birthdayFieldRef.current &&
        !birthdayFieldRef.current.contains(e.target)
      ) {
        setBirthdayPopoverOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setBirthdayPopoverOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [birthdayPopoverOpen]);

  const handleGenderChange = (event) => {
    const { name, checked } = event.target;

    setGender({
      male: name === "male" ? checked : false,
      female: name === "female" ? checked : false,
    });
  };

  const validateNickname = (e) => {
    const nickname = e.target.value;
    setNickname(nickname);
  };
  const validateAddres = (e) => {
    const address = e.target.value;
    setAddress(address);
  };

  const validateEmail = (e) => {
    const email = e.target.value;
    setEmail(email);
    // Clear a stale duplicate/format error as soon as the admin starts fixing it — otherwise
    // the red-highlighted field + message would linger even after the value has changed.
    if (emailError) setEmailError("");
  };
  const validatePhone = (e) => {
    const phone = e.target.value;
    setPhone(phone);
  };

  const [copyingResetLink, setCopyingResetLink] = useState(false);
  // Generates the client's password-reset link server-side (same guid-based link the SMS/email
  // forgot-password flows send) and copies it to the clipboard — nothing is sent automatically,
  // the office staff pastes it into whatever channel they want themselves.
  const handleCopyResetLink = async () => {
    const id = activeClientInformation?.client_id || activeClientInformation?.id;
    if (!id) return;
    setCopyingResetLink(true);
    try {
      const token = localStorage.getItem("token");
      const { link } = await ApiMethods.get(
        `client/${id}/reset-password-link`,
        token
      );
      await navigator.clipboard.writeText(link);
      showAlert("Link je kopiran u clipboard", "success");
    } catch (error) {
      showAlert("Greška prilikom generisanja linka", "error");
    } finally {
      setCopyingResetLink(false);
    }
  };

  const validateFirstName = (e) => {
    const firstName = e.target.value;
    setFirstName(firstName);
  };

  const validateLastName = (e) => {
    const lastName = e.target.value;
    setLastName(lastName);
  };
  const getChangedFields = () => {
    const currentValues = {
      gender: gender.male ? "Male" : gender.female ? "Female" : null,
      birthday,
      email,
      first_name: firstName,
      nickname,
      notes,
      last_name: lastName,
      phone,
      address,
      height,
      weight,
    };

    const data = Object.keys(currentValues).reduce((acc, key) => {
      if (key !== "gender" && currentValues[key] !== initialValues[key]) {
        acc[key] = currentValues[key];
      } else if (
        key === "gender" &&
        currentValues[key] !== initialValues.gender
      ) {
        acc[key] = currentValues[key];
      }
      return acc;
    }, {});

    // height/weight are tracked as strings (see initialValues) so they bind to plain <input>s,
    // but the backend column is numeric — convert on the way out, empty string means "cleared".
    if (Object.prototype.hasOwnProperty.call(data, "height")) {
      data.height = data.height === "" ? null : Number(data.height);
    }
    if (Object.prototype.hasOwnProperty.call(data, "weight")) {
      data.weight = data.weight === "" ? null : Number(data.weight);
    }

    return data;
  };

  const validateHeight = (e) => {
    setHeight(e.target.value);
  };

  const validateWeight = (e) => {
    setWeight(e.target.value);
  };

  // Birthday/Height/Weight are all optional, but if entered they must be sane. Shared by
  // onSave/onEdit so both paths enforce the exact same rules.
  const validateOptionalFields = () => {
    if (birthday) {
      const parsedBirthday = moment(birthday, "YYYY-MM-DD", true);
      if (!parsedBirthday.isValid()) {
        showAlert("Datum rođenja nije validan", "error");
        return false;
      }
      if (parsedBirthday.isAfter(moment(), "day")) {
        showAlert("Datum rođenja ne može biti u budućnosti", "error");
        return false;
      }
    }
    if (height !== "" && (isNaN(Number(height)) || Number(height) <= 0)) {
      showAlert("Visina mora biti pozitivan broj", "error");
      return false;
    }
    if (weight !== "" && (isNaN(Number(weight)) || Number(weight) <= 0)) {
      showAlert("Težina mora biti pozitivan broj", "error");
      return false;
    }
    return true;
  };

  // X always just closes this layer without saving — `onCancel` (when the caller distinguishes
  // it, e.g. NewAppointmentFlow's nested Add Member) skips the parent's create/refetch logic
  // entirely; callers that don't pass it keep the old behavior (`openPopup()` with no args).
  const handleCancel = () => {
    blurActiveElement();
    (onCancel || openPopup)();
  };

  const onSave = async (e) => {
    if (firstName === "") {
      showAlert("Nije popunjemo polje za ime", "error");
      return;
    }
    if (gender === "") {
      showAlert("Niste oznacili pol", "error");
      return;
    }

    if (lastName === "") {
      showAlert("Nije popunjemo polje za prezime", "error");
      return;
    }

    // Immediate client-side feedback only — the server-side check on save is the real,
    // final source of truth for uniqueness (this can't know about other members' emails).
    if (email && !validator.isEmail(email)) {
      setEmailError("Unesite validnu email adresu.");
      return;
    }

    if (!validateOptionalFields()) return;

    const google_token = localStorage.getItem("googleAccessToken");
    console.log(google_token, "Google token");
    try {
      const data = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        nickname: nickname,
        notes: notes,
        phone: phone,
        address: address,
        googleAccessToken: google_token,
        gender: gender,
        birthday: birthday,
        height: height !== "" ? Number(height) : undefined,
        weight: weight !== "" ? Number(weight) : undefined,
      };
      if (gender.male) {
        data.gender = "male";
      } else if (gender.female) {
        data.gender = "female";
      } else {
        showAlert("Niste oznacili pol", "error");
        console.error("Invalid gender selected.");
        return;
      }
      const response = await ApiMethods.post("auth/add_client", data);
      console.log(response, "resdadas");
      showAlert("Uspesno ste dodali klijenta", "success");
      // Pass the newly created client (if the backend returned it) through
      // so callers can add it straight to the shared member cache instead
      // of refetching the whole list.
      blurActiveElement();
      openPopup(response?.client || null);
      setActiveClientInformation(undefined);
    } catch (error) {
      const message = extractErrorMessage(error);
      if (message === DUPLICATE_EMAIL_MESSAGE) {
        // Keep the popup open with everything the admin already entered — only the Email
        // field is wrong, so highlight just that instead of a generic toast + form reset.
        setEmailError(
          "Ova email adresa je vec u upotrebi. Unesite drugu email adresu."
        );
      } else {
        showAlert("Neuspesno dodavanje klijenta", "error");
      }
      console.error("Error during registration:", error);
    }
  };

  const onEdit = async (e) => {
    if (firstName === "") {
      showAlert("Nije popunjemo polje za ime", "error");

      return;
    }
    if (gender === "") {
      showAlert("Niste oznacili pol", "error");

      return;
    }

    if (lastName === "") {
      showAlert("Nije popunjemo polje za prezime", "error");

      return;
    }

    // Email is read-only in edit mode (can't have changed), so no format re-check needed here.

    if (!validateOptionalFields()) return;

    try {
      const changedData = getChangedFields();
      if (!changedData.gender) {
        showAlert("Niste oznacili pol", "error");
        return;
      }

      if (changedData.gender === activeClientInformation.gender) {
        delete changedData.gender;
      }
      if (Object.values(changedData).length === 0) {
        showAlert("Nema promena", "info");
        return;
      }
      const id =
        activeClientInformation.client_id || activeClientInformation.id;
      console.log(id, "ovo je idd");
      console.log(changedData);

      const google_token = localStorage.getItem("googleAccessToken");
      changedData.googleToken = google_token;

      const response = await ApiMethods.post(`client/${id}`, changedData);
      console.log(response, "resdadas");

      const updatedClientInformation = {
        ...activeClientInformation,
        ...changedData,
      };
      setActiveClientInformation(updatedClientInformation);
      showAlert("Uspesno ste editovali klijenta", "success");
      // Pass the merged record through so callers can upsert just this one
      // client into the shared cache instead of refetching everything.
      refresh(updatedClientInformation);
      blurActiveElement();
      openPopup();
    } catch (error) {
      const message = extractErrorMessage(error);
      if (message === DUPLICATE_EMAIL_MESSAGE) {
        // Same as onSave: keep the popup open with the entered values intact, only flag
        // the Email field instead of a generic toast + closing/clearing everything.
        setEmailError(
          "Ova email adresa je vec u upotrebi. Unesite drugu email adresu."
        );
      } else {
        showAlert("Doslo je do greske", "error");
      }
      console.error("Error during registration:", error);
    }
  };

  return (
    <div className="hf-mp-overlay">
      <div className="hf-mp-card">
        <IconButton
          className="hf-mp-close"
          aria-label="Zatvori"
          onClick={handleCancel}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <div className="hf-mp-header">
          <button
            type="button"
            className="hf-mp-avatar-wrap"
            aria-label="Promeni profilnu sliku"
            onClick={() => showAlert("Promena profilne slike uskoro", "info")}
          >
            <span className="hf-mp-avatar">{initials || "?"}</span>
            <span className="hf-mp-avatar-camera">
              <PhotoCameraIcon sx={{ fontSize: 13 }} />
            </span>
          </button>
          <div className="hf-mp-header-text">
            <div className="hf-mp-name">
              {firstName || lastName
                ? `${firstName} ${lastName}`.trim()
                : "Novi član"}
            </div>
            <div className="hf-mp-role">Član</div>
          </div>
        </div>

        {/* Scrollable middle section — on Mobile, .hf-mp-header above and .hf-mp-save-btn below
            stay pinned in place (memberPopup.css's mobile media query) while only this fills
            fields section scrolls, so the avatar/name/close button and the save action are
            always reachable no matter how far down the form the admin has scrolled. Desktop/
            Tablet is untouched — this wrapper adds no layout behavior there, the whole card
            still scrolls as one piece like before. */}
        <div className="hf-mp-body">
        {/* Desktop/Tablet: Ime/Prezime/Nadimak, Email/Adresa/Pol, Telefon/Datum rođenja,
            Visina/Težina — .hf-mp-row-3 rows are 3-across (memberPopup.css). Mobile collapses
            every row (including the 3-across ones) to a single stacked column, same as before. */}
        <div className="hf-mp-row hf-mp-row-3">
          <div className="hf-mp-field">
            <label className="hf-mp-label">Ime *</label>
            {/* No `autoFocus` here (used to have one) — this popup is also opened read-mostly
                from Calendar/Members "Pogledaj profil", not just "add new member", and
                autofocusing this input immediately triggered memberPopup.css's Mobile
                `.hf-mp-card:focus-within` rule (meant for "the real keyboard is open"), shrinking
                the card to 55vh from the moment it opened — with nothing having actually been
                tapped yet. That cut the form off well before "Adresa" with no way to scroll
                further, on every single open. */}
            <input
              className="hf-mp-input"
              value={firstName}
              onChange={validateFirstName}
            />
          </div>
          <div className="hf-mp-field">
            <label className="hf-mp-label">Prezime *</label>
            <input
              className="hf-mp-input"
              value={lastName}
              onChange={validateLastName}
            />
          </div>
          <div className="hf-mp-field">
            <label className="hf-mp-label">Nadimak</label>
            <input
              className="hf-mp-input"
              placeholder="npr. Jordi"
              value={nickname}
              onChange={validateNickname}
            />
          </div>
        </div>

        <div className="hf-mp-row hf-mp-row-3">
          <div className="hf-mp-field">
            <label className="hf-mp-label">Email adresa</label>
            <div className="hf-mp-input-icon-wrap">
              <EmailIcon className="hf-mp-input-icon" sx={{ fontSize: 16 }} />
              <input
                className={`hf-mp-input hf-mp-input-with-icon${
                  emailError ? " hf-mp-input-error" : ""
                }`}
                type="email"
                value={email}
                onChange={isEditMode ? undefined : validateEmail}
                readOnly={isEditMode}
                aria-invalid={!!emailError}
              />
            </div>
            {isEditMode ? (
              <p className="hf-mp-helper-text">
                Email adresa se ne može menjati
              </p>
            ) : (
              emailError && <p className="hf-mp-error-text">{emailError}</p>
            )}
          </div>
          <div className="hf-mp-field">
            <label className="hf-mp-label">Adresa</label>
            <div className="hf-mp-input-icon-wrap">
              <AddLocationAltIcon
                className="hf-mp-input-icon"
                sx={{ fontSize: 16 }}
              />
              <input
                className="hf-mp-input hf-mp-input-with-icon"
                value={address}
                onChange={validateAddres}
              />
            </div>
          </div>
          <div className="hf-mp-field">
            <label className="hf-mp-label">Pol *</label>
            <div className="hf-mp-gender-row">
              <button
                type="button"
                className={`hf-mp-gender-chip${gender.male ? " active" : ""}`}
                onClick={() =>
                  handleGenderChange({
                    target: { name: "male", checked: !gender.male },
                  })
                }
              >
                Muško
              </button>
              <button
                type="button"
                className={`hf-mp-gender-chip${
                  gender.female ? " active" : ""
                }`}
                onClick={() =>
                  handleGenderChange({
                    target: { name: "female", checked: !gender.female },
                  })
                }
              >
                Žensko
              </button>
            </div>
          </div>
        </div>

        <div className="hf-mp-row">
          <div className="hf-mp-field">
            <label className="hf-mp-label">Broj telefona</label>
            <div className="hf-mp-input-icon-wrap">
              <LocalPhoneIcon
                className="hf-mp-input-icon"
                sx={{ fontSize: 16 }}
              />
              <input
                className="hf-mp-input hf-mp-input-with-icon"
                type="phone"
                value={phone}
                onChange={validatePhone}
              />
              {isEditMode && phone && (
                <IconButton
                  onClick={handleCopyResetLink}
                  disabled={copyingResetLink}
                  aria-label="Kopiraj link za reset lozinke"
                  title="Kopiraj link za reset lozinke"
                  sx={{ flexShrink: 0 }}
                >
                  <LinkIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </div>
          </div>
          <div className="hf-mp-field">
            <label className="hf-mp-label">Datum rođenja</label>
            <div
              className="hf-naf-d-field-wrap hf-mp-date-field-wrap"
              ref={birthdayFieldRef}
            >
              <button
                type="button"
                className="hf-mp-input hf-mp-date-trigger"
                onClick={() => setBirthdayPopoverOpen((open) => !open)}
              >
                <span className={birthday ? "" : "hf-mp-date-placeholder"}>
                  {birthday
                    ? moment(birthday, "YYYY-MM-DD").format("DD.MM.YYYY")
                    : "Izaberi datum"}
                </span>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />
              </button>
              {birthdayPopoverOpen && (
                <div className="hf-naf-d-popover hf-mp-date-popover-above">
                  <MonthCalendarGrid
                    viewMonth={birthdayViewMonth}
                    onPrevMonth={() =>
                      setBirthdayViewMonth((m) =>
                        m.clone().subtract(1, "month")
                      )
                    }
                    onNextMonth={() =>
                      setBirthdayViewMonth((m) => m.clone().add(1, "month"))
                    }
                    monthDays={birthdayMonthDays}
                    today={moment()}
                    selectedDate={
                      birthday ? moment(birthday, "YYYY-MM-DD") : null
                    }
                    isSelectableDay={(day) => !day.isAfter(moment(), "day")}
                    onSelectDay={(day) => {
                      setBirthday(day.format("YYYY-MM-DD"));
                      setBirthdayPopoverOpen(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="hf-mp-row">
          <div className="hf-mp-field">
            <label className="hf-mp-label">Visina</label>
            <div className="hf-mp-input-suffix-wrap">
              <input
                className="hf-mp-input hf-mp-input-with-suffix"
                type="number"
                min="0"
                step="0.1"
                placeholder="182"
                value={height}
                onChange={validateHeight}
              />
              <span className="hf-mp-input-suffix">cm</span>
            </div>
          </div>
          <div className="hf-mp-field">
            <label className="hf-mp-label">Težina</label>
            <div className="hf-mp-input-suffix-wrap">
              <input
                className="hf-mp-input hf-mp-input-with-suffix"
                type="number"
                min="0"
                step="0.1"
                placeholder="84"
                value={weight}
                onChange={validateWeight}
              />
              <span className="hf-mp-input-suffix">kg</span>
            </div>
          </div>
        </div>
        </div>

        <button
          type="button"
          onClick={() => (!activeClientInformation ? onSave() : onEdit())}
          className="hf-mp-save-btn"
        >
          {activeClientInformation ? "Sačuvaj izmene" : "Sačuvaj člana"}
        </button>
      </div>
    </div>
  );
};

export default PopupTest;
